import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Download,
  TrendingUp,
  CheckCircle,
  Star,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface PerformanceData {
  rating: number;
  completionRate: number;
  onTimeRate: number;
  repeatCustomers: number;
  totalJobs: number;
  completedJobs: number;
  totalEarnedGbp: string;
  overallStatus: 'excellent' | 'good' | 'average' | 'needs_improvement';
}

const STATUS_LABEL: Record<string, string> = {
  excellent: 'EXCELLENT PERFORMANCE',
  good: 'GOOD PERFORMANCE',
  average: 'AVERAGE PERFORMANCE',
  needs_improvement: 'NEEDS IMPROVEMENT',
};

const ProviderPerformanceReportsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [performance, setPerformance] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchPerformanceData();
    }
  }, [user?.id, authLoading]);

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/performance`);
      if (response.success && response.data) {
        setPerformance(response.data);
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load performance data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!performance) return;
    const rows = [
      ['Metric', 'Value'],
      ['Completion Rate', `${performance.completionRate}%`],
      ['On-Time Rate', `${performance.onTimeRate}%`],
      ['Total Jobs', String(performance.totalJobs)],
      ['Completed Jobs', String(performance.completedJobs)],
      ['Repeat Customers', String(performance.repeatCustomers)],
      ['Average Rating', performance.rating > 0 ? performance.rating.toFixed(1) : 'Not yet rated'],
      ['Total Earned', `£${performance.totalEarnedGbp}`],
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'performance-report.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Exported', description: 'Performance report exported as CSV' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'good':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'average':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'needs_improvement':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Performance Reports - VireWire Provider</title>
        <meta name="description" content="View your performance metrics and reports" />
      </Helmet>

      <div className="space-y-8">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : performance ? (
          <>
            {/* Header with Action Buttons */}
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Performance</h1>
                <p className="text-gray-600 text-sm mt-1">Provider Overview</p>
              </div>
              <Button
                onClick={handleExportCsv}
                className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>

            {/* Performance Status */}
            <Card className={`border-2 ${getStatusColor(performance.overallStatus)}`}>
              <CardContent className="p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 mb-2">
                      {STATUS_LABEL[performance.overallStatus] || 'PERFORMANCE'}
                    </p>
                    <p className="text-lg text-gray-600">
                      Based on {performance.totalJobs} job{performance.totalJobs === 1 ? '' : 's'} assigned to you
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-white border border-gray-200">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-green-600">
                    {performance.completionRate}%
                  </p>
                  <p className="text-sm text-gray-600 mt-2">Completion Rate</p>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-blue-600">
                    {performance.onTimeRate}%
                  </p>
                  <p className="text-sm text-gray-600 mt-2">On-Time Rate</p>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-purple-600">
                    {performance.repeatCustomers}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">Repeat Customers</p>
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardContent className="p-6 text-center">
                  {performance.rating > 0 ? (
                    <p className="text-4xl font-bold text-yellow-600 flex items-center justify-center gap-1">
                      <Star className="w-6 h-6 fill-yellow-500 text-yellow-500" />
                      {performance.rating.toFixed(1)}
                    </p>
                  ) : (
                    <p className="text-lg font-semibold text-gray-400">Not yet rated</p>
                  )}
                  <p className="text-sm text-gray-600 mt-2">Average Rating</p>
                </CardContent>
              </Card>
            </div>

            {/* Job totals */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-lg font-bold">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Job Totals
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Jobs Assigned</span>
                    <span className="text-2xl font-bold text-gray-900">{performance.totalJobs}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Jobs Completed</span>
                    <span className="text-2xl font-bold text-gray-900">{performance.completedJobs}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Earned */}
            <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white border-0">
              <CardContent className="p-8">
                <p className="text-green-100 text-sm font-semibold mb-2">TOTAL AMOUNT EARNED</p>
                <p className="text-5xl font-bold">£{Number(performance.totalEarnedGbp).toLocaleString()}</p>
                <p className="text-green-100 text-sm mt-2">From completed jobs, after platform fee</p>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600">No performance data found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderPerformanceReportsPage;
